import React, { useState, useEffect } from 'react';

export const HeroRotating = ({
  textBefore = "Real",
  textLineOne = "Patients Can",
  textLineTwo = "Transform Healthcare",
  condition1 = "Cancer",
  condition1Color = "#385cbc",
  condition2 = "Diabetes",
  condition2Color = "#f15b41",
  condition3 = "Asthma",
  condition3Color = "#819cd1",
  condition4 = "IBD",
  condition4Color = "#fab184",
  condition5 = "MDD",
  condition5Color = "#6b8e23",
  condition6 = "Lupus",
  condition6Color = "#9b59b6",
  condition7 = "Allergy",
  condition7Color = "#e67e22",
  subheadline = "Real insight comes from real experiences. Start by sharing yours.",
  ctaText = "Share Your Story",
  ctaLink = "#",
  onCtaClick,
  secondaryCtaText = "How It Works",
  onSecondaryCtaClick,
  imageUrl = "",
  backgroundColor = "#faf8f6",
  ctaBackgroundColor = "#fff1b2",
  textColor = "#272726",
  minHeight = "80vh",
  paddingTop = "180px",
  paddingBottom = "100px",
  rotationSpeed = 2500
}) => {
  const conditionsData = [
    { text: condition1, color: condition1Color },
    { text: condition2, color: condition2Color },
    { text: condition3, color: condition3Color },
    { text: condition4, color: condition4Color },
    { text: condition5, color: condition5Color },
    { text: condition6, color: condition6Color },
    { text: condition7, color: condition7Color }
  ].filter(item => item.text);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [highlightProgress, setHighlightProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      // Reset highlight when fading out
      setHighlightProgress(0);
      setIsVisible(false);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % conditionsData.length);
        setIsVisible(true);
        // Start highlight animation after 0.5s delay
        setTimeout(() => {
          setHighlightProgress(100);
        }, 500);
      }, 300);
    }, rotationSpeed);

    // Initial highlight on first load
    setTimeout(() => {
      setHighlightProgress(100);
    }, 500);

    return () => clearInterval(interval);
  }, [conditionsData.length, rotationSpeed]);

  // Clean the imageUrl - remove any whitespace/line breaks that Webflow might add
  const cleanImageUrl = imageUrl ? imageUrl.replace(/\s/g, '') : '';

  // Text-focused hero - no image, copy is the star
  if (!cleanImageUrl) {
    return (
      <section
        className="hero-section-mobile"
        style={{
          minHeight: minHeight,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: `${paddingTop} 20px ${paddingBottom}`,
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(180deg, ${backgroundColor} 0%, #fff 100%)`
        }}
      >
        {/* Animated floating shapes */}
        <style>
          {`
            @keyframes floatBlob1 {
              0%, 100% { transform: translate(0, 0) scale(1); }
              33% { transform: translate(30px, -20px) scale(1.05); }
              66% { transform: translate(-20px, 20px) scale(0.95); }
            }
            @keyframes floatBlob2 {
              0%, 100% { transform: translate(0, 0) scale(1); }
              33% { transform: translate(-40px, 30px) scale(0.95); }
              66% { transform: translate(20px, -30px) scale(1.05); }
            }
            @keyframes floatBlob3 {
              0%, 100% { transform: translate(0, 0) rotate(0deg); }
              50% { transform: translate(25px, 25px) rotate(10deg); }
            }
            @keyframes floatGeo1 {
              0%, 100% { transform: translate(0, 0) rotate(0deg); }
              50% { transform: translate(-15px, 10px) rotate(5deg); }
            }
            @keyframes floatGeo2 {
              0%, 100% { transform: translate(0, 0) rotate(0deg); }
              50% { transform: translate(10px, -15px) rotate(-3deg); }
            }
            @keyframes floatGeo3 {
              0%, 100% { transform: translate(0, 0) rotate(0deg); }
              50% { transform: translate(8px, 12px) rotate(4deg); }
            }
          `}
        </style>

        {/* Floating prism decorations - matching partner page style */}
        <div style={{ position: 'absolute', top: '12%', left: '6%', width: '100px', height: '70px', background: 'linear-gradient(135deg, #FFE4D9 0%, #FFCBB8 100%)', opacity: 0.25, transform: 'rotate(-12deg)', clipPath: 'polygon(0% 0%, 85% 0%, 100% 100%, 15% 100%)', zIndex: 0, animation: 'floatGeo1 20s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', top: '20%', right: '8%', width: '85px', height: '55px', background: 'linear-gradient(135deg, #FFCBB8 0%, #FFB4A0 100%)', opacity: 0.2, transform: 'rotate(18deg)', clipPath: 'polygon(0% 0%, 100% 0%, 90% 100%, 10% 100%)', zIndex: 0, animation: 'floatGeo2 25s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: '25%', left: '4%', width: '75px', height: '90px', background: 'linear-gradient(135deg, #FFB4A0 0%, #E8927C 100%)', opacity: 0.18, transform: 'rotate(25deg)', clipPath: 'polygon(20% 0%, 100% 0%, 80% 100%, 0% 100%)', zIndex: 0, animation: 'floatGeo3 18s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: '35%', right: '6%', width: '95px', height: '60px', background: 'linear-gradient(135deg, #E8927C 0%, #D4796A 100%)', opacity: 0.15, transform: 'rotate(-20deg)', clipPath: 'polygon(10% 0%, 90% 0%, 100% 100%, 0% 100%)', zIndex: 0, animation: 'floatGeo1 22s ease-in-out infinite reverse' }} />
        <div style={{ position: 'absolute', top: '45%', left: '3%', width: '90px', height: '32px', background: 'linear-gradient(135deg, #B5C9A8 0%, #B5C9A8 100%)', opacity: 0.15, transform: 'rotate(30deg)', clipPath: 'polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)', zIndex: 0, animation: 'floatGeo2 20s ease-in-out infinite reverse' }} />
        <div style={{ position: 'absolute', top: '65%', right: '5%', width: '105px', height: '35px', background: 'linear-gradient(135deg, #FFE4D9 0%, #FFB4A0 100%)', opacity: 0.12, transform: 'rotate(-28deg)', clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)', zIndex: 0, animation: 'floatGeo3 24s ease-in-out infinite' }} />

        {/* Content */}
        <div
          style={{
            maxWidth: '1100px',
            textAlign: 'center',
            position: 'relative',
            zIndex: 1
          }}
        >
          <h1
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(56px, 10vw, 120px)',
              fontWeight: '700',
              color: textColor,
              marginTop: '0',
              marginBottom: '40px',
              lineHeight: '1.05'
            }}
          >
            {/* Line 1: "Real" - fixed, unbold */}
            <span style={{ display: 'block', fontWeight: '400' }}>{textBefore}</span>
            {/* Line 2: Condition - animated */}
            <span style={{ display: 'block' }}>
              <span
                className="notranslate"
                style={{
                  color: textColor,
                  display: 'inline-block',
                  position: 'relative',
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(-20px)',
                  transition: 'opacity 0.3s ease, transform 0.3s ease'
                }}
              >
                {/* Highlighter effect */}
                <span
                  style={{
                    position: 'absolute',
                    left: '-3%',
                    right: '-3%',
                    bottom: '-5%',
                    height: '65%',
                    background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.85) 0%, rgba(245, 185, 165, 0.8) 40%, rgba(250, 200, 160, 0.75) 70%, rgba(245, 215, 170, 0.6) 100%)',
                    transform: 'skewX(-1.5deg) rotate(-1.2deg)',
                    transformOrigin: 'left center',
                    zIndex: -1,
                    width: `${highlightProgress}%`,
                    transition: 'width 1.05s cubic-bezier(0.4, 0, 0.2, 1)',
                    borderRadius: '2px 4px 3px 2px',
                    clipPath: 'polygon(0% 0%, 97% 2%, 100% 15%, 98% 35%, 100% 55%, 97% 75%, 100% 90%, 96% 100%, 0% 100%)'
                  }}
                />
                {conditionsData[currentIndex]?.text}
              </span>
            </span>
            {/* Line 3: "Patients" - fixed, unbold */}
            <span style={{ display: 'block', fontWeight: '400' }}>Patients</span>
          </h1>

          <p
            style={{
              fontSize: 'clamp(18px, 2.5vw, 24px)',
              lineHeight: '1.6',
              color: textColor,
              maxWidth: '700px',
              margin: '0 auto 48px auto',
              opacity: 0.8
            }}
          >
            <span style={{ display: 'block', marginBottom: '8px' }}>
              Real insight comes from{' '}
              <span style={{ position: 'relative', display: 'inline-block' }}>
                <span
                  style={{
                    position: 'absolute',
                    left: '-2%',
                    right: '-2%',
                    bottom: '0',
                    height: '40%',
                    background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.7) 0%, rgba(245, 185, 165, 0.65) 40%, rgba(250, 200, 160, 0.6) 70%, rgba(245, 215, 170, 0.5) 100%)',
                    transform: 'skewX(-1deg)',
                    zIndex: 0,
                    borderRadius: '2px'
                  }}
                />
                <span style={{ position: 'relative', zIndex: 1 }}>real</span>
              </span>
              {' '}experiences.
            </span>
            <span style={{ display: 'block' }}>Start by sharing yours.</span>
          </p>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px'
            }}
          >
            {/* Mobile-responsive styles for hero */}
            <style>{`
              .hero-cta-container {
                display: flex;
                flex-wrap: wrap;
                justify-content: center;
                gap: 16px;
              }
              .hero-cta-button {
                display: inline-block;
                font-size: 18px;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 1px;
                padding: 20px 48px;
                cursor: pointer;
                transition: transform 0.2s ease, background-color 0.2s ease;
              }
              @media (max-width: 600px) {
                .hero-section-mobile {
                  padding-top: 310px !important;
                }
                .hero-cta-button {
                  padding: 14px 28px !important;
                  font-size: 14px !important;
                  width: 100%;
                  text-align: center;
                }
                .hero-cta-container {
                  flex-direction: column;
                  width: 100%;
                  max-width: 280px;
                }
              }
            `}</style>

            <div className="hero-cta-container">
              {onCtaClick ? (
                <button
                  onClick={onCtaClick}
                  className="hero-cta-button"
                  style={{
                    backgroundColor: ctaBackgroundColor,
                    color: textColor,
                    border: `2px solid ${textColor}`
                  }}
                >
                  {ctaText}
                </button>
              ) : (
                <a
                  href={ctaLink}
                  className="hero-cta-button"
                  style={{
                    backgroundColor: ctaBackgroundColor,
                    color: textColor,
                    textDecoration: 'none',
                    border: `2px solid ${textColor}`
                  }}
                >
                  {ctaText}
                </a>
              )}
              {onSecondaryCtaClick && (
                <button
                  onClick={onSecondaryCtaClick}
                  className="hero-cta-button"
                  style={{
                    backgroundColor: 'transparent',
                    color: textColor,
                    border: `2px solid ${textColor}`,
                    fontFamily: 'inherit'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  {secondaryCtaText}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Wavy bottom divider */}
        <svg
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '80px',
            pointerEvents: 'none'
          }}
          viewBox="0 0 1440 80"
          preserveAspectRatio="none"
        >
          <path
            d="M0,40 C150,80 350,0 600,40 C850,80 1100,0 1280,40 C1350,55 1400,50 1440,40 L1440,80 L0,80 Z"
            fill="#f7f5f3"
          />
        </svg>
      </section>
    );
  }

  // With image - flexbox side by side layout
  return (
    <section
      style={{
        minHeight: minHeight,
        backgroundColor: backgroundColor,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch'
      }}
    >
      {/* Left side - Image with gradient fade */}
      <div
        style={{
          width: '55%',
          minHeight: minHeight,
          position: 'relative',
          flexShrink: 0
        }}
      >
        <img
          src={cleanImageUrl}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            display: 'block'
          }}
        />
        {/* Gradient fade overlay on right edge */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: `linear-gradient(to right, transparent 0%, ${backgroundColor} 100%)`,
            pointerEvents: 'none'
          }}
        />
      </div>

      {/* Right side - Text content */}
      <div
        style={{
          width: '45%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          padding: `${paddingTop} 40px ${paddingBottom}`,
          boxSizing: 'border-box'
        }}
      >
        <h1
          style={{
            fontFamily: '"Source Serif Pro", Georgia, serif',
            fontSize: 'clamp(28px, 3.5vw, 52px)',
            fontWeight: '700',
            color: textColor,
            marginTop: '0',
            marginBottom: '20px',
            lineHeight: '1.15'
          }}
        >
          <span style={{ whiteSpace: 'nowrap' }}>
            {textBefore}{' '}
            <span
              className="notranslate"
              style={{
                color: textColor,
                display: 'inline-block',
                position: 'relative',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(-20px)',
                transition: 'opacity 0.3s ease, transform 0.3s ease'
              }}
            >
              {/* Highlighter effect */}
              <span
                style={{
                  position: 'absolute',
                  left: '-3%',
                  right: '-3%',
                  bottom: '-5%',
                  height: '65%',
                  background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.85) 0%, rgba(245, 185, 165, 0.8) 40%, rgba(250, 200, 160, 0.75) 70%, rgba(245, 215, 170, 0.6) 100%)',
                  transform: 'skewX(-1.5deg) rotate(-1.2deg)',
                  transformOrigin: 'left center',
                  zIndex: -1,
                  width: `${highlightProgress}%`,
                  transition: 'width 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  borderRadius: '2px 4px 3px 2px',
                  clipPath: 'polygon(0% 0%, 97% 2%, 100% 15%, 98% 35%, 100% 55%, 97% 75%, 100% 90%, 96% 100%, 0% 100%)'
                }}
              />
              {conditionsData[currentIndex]?.text}
            </span>
            {' '}{textLineOne}
          </span>
          <br />
          <span>{textLineTwo}</span>
        </h1>

        <p
          style={{
            fontSize: '18px',
            lineHeight: '1.6',
            color: textColor,
            maxWidth: '450px',
            margin: '0 auto 28px auto'
          }}
        >
          {subheadline}
        </p>

        <a
          href={ctaLink}
          style={{
            display: 'inline-block',
            backgroundColor: ctaBackgroundColor,
            color: textColor,
            fontSize: '14px',
            fontWeight: '700',
            textTransform: 'uppercase',
            textDecoration: 'none',
            padding: '16px 48px',
            border: `2px solid ${textColor}`,
            cursor: 'pointer',
            transition: 'transform 0.2s ease'
          }}
        >
          {ctaText}
        </a>
      </div>
    </section>
  );
};
